import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router';

import { EducationInquiryCard } from '@/widgets/education-inquiry-card';
import { SurveyBanner } from '@/widgets/survey-banner';
import { downloadErrorMessage, useAutoDownload } from '@/features/download-asset';
import {
  formatPublishedAt,
  useVideoBySlug,
  youtubeEmbedUrl,
  youtubeWatchUrl,
} from '@/entities/video';
import { errorCodeOf } from '@/shared/api';

import { ChapterNav } from './chapter-nav';
import { ChapterSection } from './chapter-section';

/** 로그인 복귀 후 자동 재개 상태 배너 (LIB-U-03 AC-3) — 소리 없이 성공/실패하면 고장으로 읽힌다. */
function AutoDownloadBanner({
  state,
}: {
  state: ReturnType<typeof useAutoDownload>;
}): ReactElement | null {
  if (state.resumedAssetId === null) {
    return null;
  }
  if (state.isPending) {
    return (
      <p className="rounded-lg border border-border bg-secondary/40 p-4 text-[13.5px]">
        다운로드를 준비하고 있습니다…
      </p>
    );
  }
  if (state.isError) {
    return (
      <p
        role="alert"
        className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-4 text-[13.5px]"
      >
        {downloadErrorMessage(state.error)}
        <button type="button" onClick={state.retry} className="underline underline-offset-2">
          재시도
        </button>
      </p>
    );
  }
  return (
    <p className="rounded-lg border border-border bg-secondary/40 p-4 text-[13.5px]">
      다운로드가 시작됐습니다. 받은 자료는{' '}
      <Link to="/my/downloads" className="underline underline-offset-2">
        내 자료함
      </Link>
      에서 다시 여실 수 있습니다.
    </p>
  );
}

/**
 * 영상 상세 — /library/:slug (D7 — LIB-U-02·05).
 *
 * 영상은 처음부터 끝까지 보는 물건이 아니다. 필요한 구간만 찾아 보므로
 * **타임스탬프 구간마다 제목·설명을 두고, 그 구간에서 쓰는 자료를 그 자리에서 준다.**
 */
export function VideoDetailPage(): ReactElement {
  const { slug = '' } = useParams();
  const video = useVideoBySlug(slug);
  const autoDownload = useAutoDownload();

  if (video.isPending) {
    return (
      <div aria-busy="true" className="u-container py-10">
        <div className="h-96 animate-pulse rounded-lg bg-secondary" />
      </div>
    );
  }

  if (video.isError) {
    // LIB-U-02 AC-3 — 없는 slug(404)와 서버 장애를 구분해 그린다
    const isNotFound = errorCodeOf(video.error) === 'NOT_FOUND';
    return (
      <div className="u-container py-10">
        <div className="flex flex-col items-center gap-3 rounded-lg border border-border border-dashed p-12 text-center">
          <p className="text-[15px] font-semibold">
            {isNotFound ? '영상을 찾을 수 없습니다' : '영상을 불러오지 못했습니다'}
          </p>
          {isNotFound ? (
            <Link to="/library" className="text-[13px] underline underline-offset-4">
              자료실로
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => void video.refetch()}
              className="text-[13px] underline underline-offset-4"
            >
              다시 시도
            </button>
          )}
        </div>
      </div>
    );
  }

  const detail = video.data;
  const resumePath = `/library/${detail.slug}`;
  const allAssets = [
    ...detail.chapters.flatMap((chapter) =>
      chapter.assets.map((asset) => ({ asset, chapterId: chapter.id })),
    ),
    ...detail.unassignedAssets.map((asset) => ({ asset, chapterId: null })),
  ];

  return (
    <div className="u-container grid gap-10 py-10 lg:grid-cols-[minmax(0,1fr)_17rem] lg:items-start">
      <div className="flex min-w-0 flex-col gap-8">
        <nav aria-label="현재 위치" className="text-[12.5px] text-muted-foreground">
          <Link to="/library" className="underline-offset-4 hover:underline">
            자료실
          </Link>
          <span aria-hidden="true"> / </span>
          <span>{detail.title}</span>
        </nav>

        <AutoDownloadBanner state={autoDownload} />

        <header className="flex flex-col gap-3">
          <span data-numeric="" className="font-mono text-[12px] text-muted-foreground">
            {formatPublishedAt(detail.publishedAt)}
          </span>
          <h1 className="text-[clamp(1.5rem,1.2rem+1.4vw,2.125rem)] font-bold tracking-[-0.042em]">
            {detail.title}
          </h1>
          {/* 운영자가 소개를 줄 바꿔 쓴다 — 줄바꿈만 살리고 연속 공백은 합친다 */}
          <p className="whitespace-pre-line text-[14.5px] leading-[1.8] text-muted-foreground">
            {detail.summary}
          </p>
        </header>

        {/* youtube-nocookie 를 쓴다 — 재생 전에는 시청 기록이 쌓이지 않는다 */}
        <div className="aspect-video overflow-hidden rounded-lg border border-border bg-secondary">
          <iframe
            src={youtubeEmbedUrl(detail.youtubeId)}
            title={detail.title}
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            className="size-full"
          />
        </div>

        <a
          href={youtubeWatchUrl(detail.youtubeId)}
          target="_blank"
          rel="noopener noreferrer"
          className="-mt-4 w-fit text-[13px] text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          유튜브에서 보기 ↗
        </a>

        {detail.chapters.length === 0 ? (
          <p className="rounded-lg border border-border border-dashed p-8 text-center text-[14px] text-muted-foreground">
            구간별 설명은 준비 중입니다.
          </p>
        ) : (
          <div className="flex flex-col gap-10">
            {detail.chapters.map((chapter, index) => (
              <ChapterSection
                key={chapter.id}
                index={index + 1}
                chapter={chapter}
                youtubeId={detail.youtubeId}
                resumePath={resumePath}
              />
            ))}
          </div>
        )}
      </div>

      {/* 사이드 전체를 하나로 고정한다. 내용이 화면보다 길어지면 그 안에서만 스크롤시켜
          본문 위로 넘치지 않게 한다 */}
      <aside className="flex flex-col gap-6 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
        {detail.chapters.length > 0 ? <ChapterNav chapters={detail.chapters} /> : null}

        {/* 자료실 목록과 같은 자리 — 자료 칸 바로 위 */}
        <SurveyBanner youtubeId={detail.youtubeId} variant="card" />

        <section
          aria-labelledby="video-assets-heading"
          className="flex flex-col gap-3 rounded-lg border border-border p-5"
        >
          <h2 id="video-assets-heading" className="text-[14px] font-semibold tracking-[-0.02em]">
            이 영상의 자료 {detail.assetCount}개
          </h2>
          {/* 자료 상세 페이지를 두지 않는다 — 자료는 그 자료를 쓰는 구간 안에서 설명된다 */}
          <ul className="flex flex-col gap-2">
            {allAssets.map(({ asset, chapterId }) => (
              <li key={asset.id}>
                <a
                  href={chapterId === null ? '#' : `#${chapterId}`}
                  className="block text-[13px] text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  {asset.title}
                </a>
              </li>
            ))}
          </ul>
        </section>

        <EducationInquiryCard />
      </aside>
    </div>
  );
}
